import { i18n } from 'boot/i18n';

import { DATE_FILTER_OPERATORS } from 'src/constants/filters';

import { BETWEEN, CUSTOM_DATES, CUSTOM_RANGE } from './filterStatement';

/**
 * The operator sets a criterion can be compared with, and the fixed options for a boolean one.
 *
 * Functions rather than module constants so the labels resolve when they are read, not when this
 * module is first imported.
 */

/**
 * Text comparisons. `null` is a real value here, meaning "contains", so it selects an option
 * rather than standing for none.
 *
 * @returns {{ value: string|null, label: string, isNegative?: boolean }[]}
 */
export function getTextCriteriaTypes() {
  return [
    { value: null, label: i18n.t('common.filter.criteriaTypeContainsLabel') },
    { value: 'startsWith', label: i18n.t('common.filter.criteriaTypeStartsWithLabel') },
    { value: 'isEqualTo', label: i18n.t('common.filter.criteriaTypeIsEqualToLabel') },
    { value: 'excludes', label: i18n.t('common.filter.criteriaTypeExcludesLabel'), isNegative: true },
  ];
}

/**
 * Text comparisons a given criterion allows. A criterion can narrow the set through
 * `options.allowedCriteriaTypes`; without one it gets them all.
 *
 * @param {{ options?: { allowedCriteriaTypes?: (string|null)[] } }} criterion
 */
export function getAvailableTextCriteriaTypes(criterion) {
  const allowed = criterion?.options?.allowedCriteriaTypes;
  const all = getTextCriteriaTypes();

  return allowed ? all.filter((type) => allowed.includes(type.value)) : all;
}

/**
 * Numeric comparisons, shared by numbers and durations. `shortLabel` is what ends up on the chip,
 * where "Age > 5" reads better than "Age greater than 5".
 *
 * @returns {{ value: string|null, label: string, shortLabel: string }[]}
 */
export function getNumericCriteriaTypes() {
  return [
    { value: 'lessThan', label: i18n.t('common.filter.criteriaTypeLessThanLabel'), shortLabel: '<' },
    { value: 'lessThanEqual', label: i18n.t('common.filter.criteriaTypeLessThanEqualLabel'), shortLabel: '<=' },
    { value: null, label: i18n.t('common.filter.criteriaTypeEqualsToLabel'), shortLabel: '=' },
    { value: 'greaterThanEqual', label: i18n.t('common.filter.criteriaTypeGreaterThanEqualLabel'), shortLabel: '>=' },
    { value: 'greaterThan', label: i18n.t('common.filter.criteriaTypeGreaterThanLabel'), shortLabel: '>' },
    {
      value: BETWEEN,
      label: i18n.t('common.filter.criteriaTypeBetweenLabel'),
      shortLabel: i18n.t('common.filter.criteriaTypeBetweenShortLabel'),
    },
  ];
}

/**
 * The three states a boolean filter can take. `null` means "not selected", which is a value users
 * filter on rather than an absence.
 *
 * @returns {{ value: boolean|null, label: string }[]}
 */
export function getBooleanOptions() {
  return [
    { value: true, label: i18n.t('common.buttonYes') },
    { value: false, label: i18n.t('common.buttonNo') },
    { value: null, label: i18n.t('common.notSelected') },
  ];
}

/**
 * How a date filter compares: against a window the user picks, or against a cutoff a number of
 * weeks back. Only criteria that opt in through `options.dateOperators` offer the choice, so a
 * criterion that says nothing keeps the window it has always had.
 *
 * @returns {{ value: string, label: string }[]}
 */
export function getDateOperatorOptions() {
  return [
    {
      value: DATE_FILTER_OPERATORS.IS_WITHIN,
      label: i18n.t('common.filter.dateOperatorIsWithinLabel'),
    },
    {
      value: DATE_FILTER_OPERATORS.IS_MORE_THAN,
      label: i18n.t('common.filter.dateOperatorIsMoreThanLabel'),
    },
  ];
}

/**
 * The date presets a criterion offers, in the order they are shown: an optional inherited option,
 * then the presets themselves, then the two custom ones that need the user to pick actual dates.
 *
 * @param {{ options?: { showInheritedPeriod?: boolean } }} criterion
 * @param {{ id: string, label: string }[]} periodPresets Resolved for the criterion's scope
 */
export function getPeriodPresetOptions(criterion, periodPresets = []) {
  const inherited = criterion?.options?.showInheritedPeriod
    ? [{
      value: 'inherited',
      label: i18n.t('insights.chartConfigurator.periodPresetInherit'),
      isCustom: false,
    }]
    : [];

  return [
    ...inherited,
    ...periodPresets.map((preset) => ({
      value: preset.id,
      label: preset.label,
      isCustom: false,
    })),
    {
      value: CUSTOM_DATES,
      label: i18n.t('history.filter.periodPresetCustomDatesLabel'),
      isCustom: true,
    },
    {
      value: CUSTOM_RANGE,
      label: i18n.t('history.filter.periodPresetCustomDateRangeLabel'),
      isCustom: true,
    },
  ];
}

/**
 * Scopes to ask useDateRanges for. A criterion opts into future presets through
 * `options.includeFuture`; otherwise it gets the ones that do not run forwards.
 *
 * @param {{ options?: { includeFuture?: boolean } }} criterion
 * @returns {string[]}
 */
export function getPeriodPresetScopes(criterion) {
  return criterion?.options?.includeFuture
    ? ['past', 'present', 'future', 'all']
    : ['past', 'present', 'all'];
}
